import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Shield, Copy, X, Code } from "lucide-react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";



const AiAgents = ({ aiAgents, fetchAiAgents, setActiveAiAgentId }) => {
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [isConfigLoading, setIsConfigLoading] = useState(false);

    // Modal state (Create/Edit Name & Desc)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAiAgent, setEditingAiAgent] = useState(null);
    const [formData, setFormData] = useState({ name: "", description: "", is_active: true });

    // Config View Modal state
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [configAgent, setConfigAgent] = useState(null);
    const [agentJsonData, setAgentJsonData] = useState(null);

    // Delete confirmation state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [aiAgentToDelete, setAiAgentToDelete] = useState(null);

    // Billing & usage limits state
    const [billingInfo, setBillingInfo] = useState({
        maxAiAgents: null,
        usedHumanAgentSlots: 0,
        maxHumanAgentSlots: null,
        planName: "Starter"
    });

    // Initial fetch if aiAgents are empty (safety)
    useEffect(() => {
        if (!aiAgents || aiAgents.length === 0) {
            fetchAiAgents();
        }
    }, [aiAgents, fetchAiAgents]);

    useEffect(() => {
        const loadBillingInfo = async () => {
            try {
                const userStr = sessionStorage.getItem("user");
                const user = userStr ? JSON.parse(userStr) : null;
                const tenantId = user?.tenant_id || "admin";

                const subRes = await api.request(`/api/subscription/${tenantId}`);
                let maxAiAgents = 0;
                let planName = "No Plan";
                let maxHumanAgentSlots = 0;

                const PLAN_PRESETS = {
                    free_trial: { maxAiAgents: 1, maxHumanAgents: 1 },
                    starter: { maxAiAgents: 3, maxHumanAgents: 3 },
                    growth: { maxAiAgents: 5, maxHumanAgents: 5 },
                    professional: { maxAiAgents: 7, maxHumanAgents: 7 },
                    enterprise: { maxAiAgents: 999, maxHumanAgents: 999 }
                };

                if (subRes && subRes.success && subRes.data) {
                    const planId = subRes.data.plan_id.toLowerCase();
                    planName = subRes.data.plan_name;
                    const config = PLAN_PRESETS[planId] || PLAN_PRESETS.free_trial;
                    maxAiAgents = config.maxAiAgents + (subRes.data.extra_ai_agents || 0);
                    maxHumanAgentSlots = config.maxHumanAgents + (subRes.data.extra_human_agents || 0);
                }

                let usedHumanAgentSlots = 0;
                try {
                    const usersList = await api.request("/api/users");
                    if (Array.isArray(usersList)) {
                        const humanAgentUsers = usersList.filter(u => (u.role === "human_agent" || u.role === "admin") && u.is_active !== false);
                        usedHumanAgentSlots = humanAgentUsers.length;
                    }
                } catch (userErr) {
                    console.error("Failed to load users for counting slots:", userErr);
                }

                setBillingInfo({
                    maxAiAgents,
                    usedHumanAgentSlots,
                    maxHumanAgentSlots,
                    planName
                });
            } catch (err) {
                console.error("Failed to load billing limits for AI Agents page:", err);
            }
        };

        loadBillingInfo();
    }, []);

    const handleOpenModal = (aiAgent = null) => {
        if (aiAgent) {
            setEditingAiAgent(aiAgent);
            setFormData({ name: aiAgent.name, description: aiAgent.description || "", is_active: aiAgent.is_active });
            setIsModalOpen(true);
        } else {
            const limitReached = billingInfo.maxAiAgents !== null && (aiAgents ? aiAgents.length : 0) >= billingInfo.maxAiAgents;
            if (limitReached) {
                showToast(`You have reached the maximum limit of ${billingInfo.maxAiAgents} AI Agent slots on your current plan. Please upgrade to unlock more slots.`, "error");
                return;
            }
            setEditingAiAgent(null);
            setFormData({ name: "", description: "", is_active: true });
            setIsModalOpen(true);
        }
    };

    const handleOpenConfigModal = async (aiAgent) => {
        setConfigAgent(aiAgent);
        setIsConfigModalOpen(true);
        setIsConfigLoading(true);
        setAgentJsonData(null);

        try {
            // Single aggregated call — backend runs all sub-fetches concurrently
            const fullConfig = await api.request(`/ai-agents/${aiAgent.id}/full-config`);
            setAgentJsonData(fullConfig);
        } catch (err) {
            console.error("Failed to load agent configuration:", err);
            showToast("Failed to load agent configuration", "error");
        } finally {
            setIsConfigLoading(false);
        }
    };

    const handleOpenDeleteModal = (aiAgent) => {

        setAiAgentToDelete(aiAgent);
        setIsDeleteModalOpen(true);
    };

    const handleSubmitGeneral = async (e) => {
        e.preventDefault();

        // Local Validation: Case-insensitive duplicate check
        const normalizedName = formData.name.trim().toLowerCase();
        const isDuplicate = aiAgents.some(p =>
            p.name.trim().toLowerCase() === normalizedName &&
            (!editingAiAgent || p.id !== editingAiAgent.id)
        );

        if (isDuplicate) {
            showToast(`A bot named "${formData.name}" already exists.`, "error");
            return;
        }

        try {
            if (editingAiAgent) {
                // Update
                await api.request(`/ai-agents/${editingAiAgent.id}`, {
                    method: "PUT",
                    body: JSON.stringify(formData),
                });
                showToast("AI Agent updated successfully");
            } else {
                // Create
                const res = await api.request("/ai-agents", {
                    method: "POST",
                    body: JSON.stringify({ name: formData.name, description: formData.description }),
                });
                showToast("AI Agent created successfully");

                if (res && res.id) {
                    const newAgentId = String(res.id);
                    const userStr = sessionStorage.getItem("user") || localStorage.getItem("user");
                    if (userStr) {
                        try {
                            const userData = JSON.parse(userStr);
                            if (!userData.assigned_ai_agents) {
                                userData.assigned_ai_agents = [];
                            }
                            const stringifiedList = userData.assigned_ai_agents.map(id => String(id));
                            if (!stringifiedList.includes(newAgentId)) {
                                userData.assigned_ai_agents.push(newAgentId);
                                const updatedUserStr = JSON.stringify(userData);
                                if (sessionStorage.getItem("user")) sessionStorage.setItem("user", updatedUserStr);
                                if (localStorage.getItem("user")) localStorage.setItem("user", updatedUserStr);
                            }
                        } catch (e) {
                            console.error("Failed to update user assigned agents in storage:", e);
                        }
                    }
                }
            }
            setIsModalOpen(false);
            fetchAiAgents();
        } catch (error) {
            console.error("Error saving AI agent:", error);
            const detail = error?.response?.detail || "Failed to save AI Agent";
            showToast(detail, "error");
        }
    };

    const handleDelete = async () => {
        if (!aiAgentToDelete) return;

        setIsLoading(true);
        try {
            await api.request(`/ai-agents/${aiAgentToDelete.id}`, {
                method: "DELETE",
            });
            showToast("AI Agent and all associated data deleted successfully");
            setIsDeleteModalOpen(false);
            setAiAgentToDelete(null);
            fetchAiAgents();
        } catch (error) {
            console.error("Error deleting AI agent:", error);
            showToast("Failed to delete AI Agent", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleAiAgentActive = async (aiAgent) => {
        try {
            await api.request(`/ai-agents/${aiAgent.id}`, {
                method: "PUT",
                body: JSON.stringify({
                    name: aiAgent.name,
                    description: aiAgent.description || "",
                    is_active: !aiAgent.is_active
                }),
            });
            showToast(`AI Agent "${aiAgent.name}" ${!aiAgent.is_active ? 'activated' : 'deactivated'}`);
            fetchAiAgents();
        } catch (error) {
            console.error("Error toggling AI agent active state:", error);
            showToast("Failed to update AI Agent status", "error");
        }
    };

    const userStr = sessionStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;

    return (
        <>
            <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn">
                <div className="max-w-6xl mx-auto">
                <PageHeader
                    title="AI Agents"
                    description="Create and manage AI agents with isolated knowledge bases, tools, and configuration settings."
                    actions={
                        <button
                            onClick={() => handleOpenModal()}
                            className="flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-2.5 rounded-xl hover:bg-black transition-all font-black text-sm uppercase shadow-xl shadow-gray-200 active:scale-95 border border-transparent"
                        >
                            <Plus size={18} />
                            Create New AI Agent
                        </button>
                    }
                />

                {/* Limits & Usage Banner */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 animate-slideIn">
                    {/* AI Agents Usage */}
                    <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-md shadow-gray-100/30 flex flex-col items-center justify-center text-center">
                        <div className="space-y-1">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">AI Agents Slots</p>
                            <p className="text-xl font-black text-gray-900 leading-snug">
                                {aiAgents ? aiAgents.length : 0} <span className="text-gray-300 font-normal">/</span> {billingInfo.maxAiAgents ?? "—"}
                            </p>
                        </div>
                    </div>

                    {/* Human Agents Usage */}
                    <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-md shadow-gray-100/30 flex flex-col items-center justify-center text-center">
                        <div className="space-y-1">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Human Agents</p>
                            <p className="text-xl font-black text-gray-900 leading-snug">
                                {billingInfo.usedHumanAgentSlots} <span className="text-gray-300 font-normal">/</span> {billingInfo.maxHumanAgentSlots ?? "—"}
                            </p>
                        </div>
                    </div>

                    {/* Current Subscription Plan */}
                    <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-md shadow-gray-100/30 flex flex-col items-center justify-center text-center">
                        <div className="space-y-1">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Subscription Plan</p>
                            <p className="text-xl font-black text-gray-900 leading-snug">
                                {billingInfo.planName}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 overflow-hidden animate-slideIn">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-32 gap-4">
                            <div className="w-10 h-10 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin"></div>
                            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Loading Repository...</p>
                        </div>
                    ) : !aiAgents || aiAgents.length === 0 ? (
                        <div className="p-16 text-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-[28px] border border-gray-100 flex items-center justify-center mb-6 mx-auto">
                                <Plus size={32} className="text-gray-300" />
                            </div>
                            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-2">No AI Agents Found</h3>
                            <p className="text-gray-400 max-w-xs mx-auto">Create your first AI agent to start training your models.</p>
                        </div>
                    ) : (
                        <div>
                            <table className="w-full text-left">
                                <thead className="bg-gray-50/50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Name</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap text-center">AI Agent ID</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">AI Agent Key</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Description</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Status</th>
                                        <th style={{ textAlign: 'center', paddingRight: '40px', width: '220px' }} className="pl-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {aiAgents.map((aiAgent) => {
                                        const isProtectedAiAgent = aiAgent.id === 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03' || 
                                                                   aiAgent.id === user?.ai_agent_id || 
                                                                   aiAgents.length === 1;
                                        return (
                                        <tr key={aiAgent.id} className="hover:bg-gray-50/50 transition-all group">
                                            <td className="px-6 py-6 font-bold text-gray-900 text-sm align-middle">
                                                <div className="flex items-center gap-2">
                                                    <span>{aiAgent.name}</span>
                                                    <button
                                                        onClick={() => handleOpenModal(aiAgent)}
                                                        className="text-gray-400 hover:text-gray-900 hover:scale-110 transition-all p-1"
                                                        title="Edit AI Agent Settings"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 align-middle text-center">
                                                <div className="flex items-center gap-2">
                                                    <div style={{ maxWidth: '140px' }} className="max-w-[140px] overflow-x-auto whitespace-nowrap bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 custom-scrollbar flex items-center">
                                                        <code className="text-[10px] font-mono text-gray-600 tracking-wider">
                                                            {aiAgent.id}
                                                        </code>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(aiAgent.id);
                                                            showToast("AI Agent ID copied to clipboard");
                                                        }}
                                                        className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0 bg-gray-50 border border-gray-100 rounded-lg hover:bg-gray-100"
                                                        title="Copy AI Agent ID"
                                                    >
                                                        <Copy size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 align-middle text-center">
                                                <div className="flex items-center gap-2">
                                                    <div className="max-w-[150px] overflow-x-auto whitespace-nowrap bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 custom-scrollbar flex items-center">
                                                        <code className="text-[10px] font-mono text-gray-600 tracking-wider">
                                                            {aiAgent.ai_agent_key || "GENERATING..."}
                                                        </code>
                                                    </div>
                                                    {aiAgent.ai_agent_key && (
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(aiAgent.ai_agent_key);
                                                                showToast("AI Agent Key copied to clipboard");
                                                            }}
                                                            className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0 bg-gray-50 border border-gray-100 rounded-lg hover:bg-gray-100"
                                                            title="Copy AI Agent Key"
                                                        >
                                                            <Copy size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 text-sm text-gray-500 max-w-[180px] break-words align-middle text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <span>{aiAgent.description || "No description provided"}</span>
                                                    <button
                                                        onClick={() => handleOpenModal(aiAgent)}
                                                        className="text-gray-400 hover:text-gray-900 hover:scale-110 transition-all p-1 flex-shrink-0"
                                                        title="Edit AI Agent Settings"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 align-middle text-center">
                                                <div className="flex justify-center">
                                                    {isProtectedAiAgent ? (
                                                        <div
                                                            className="w-11 h-6 rounded-full border-2 border-blue-100 bg-blue-50/50 flex-shrink-0 relative cursor-not-allowed opacity-60"
                                                            title="System AI Agent is permanently active"
                                                        >
                                                            <div className="absolute top-[2px] left-[22px] w-4 h-4 rounded-full bg-[#83bbe1]" />
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => toggleAiAgentActive(aiAgent)}
                                                            className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white flex-shrink-0 ${aiAgent.is_active ? 'border-[#83bbe1]' : 'border-gray-200'}`}
                                                            title={aiAgent.is_active ? "Deactivate AI Agent" : "Activate AI Agent"}
                                                        >
                                                            <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${aiAgent.is_active ? 'left-[22px] bg-[#83bbe1]' : 'left-[2px] bg-gray-300'}`} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center', paddingRight: '40px', width: '220px' }} className="pl-6 py-6 align-middle">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => handleOpenConfigModal(aiAgent)}
                                                        className="w-10 h-10 flex items-center justify-center text-gray-400 bg-white border border-gray-100 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all shadow-sm"
                                                        title="View JSON Config"
                                                    >
                                                        <Code size={18} />
                                                    </button>
                                                    {isProtectedAiAgent ? (
                                                        <div
                                                            className="w-10 h-10 flex items-center justify-center text-gray-400 bg-gray-50 rounded-xl border border-gray-100 cursor-help"
                                                            title="System AI Agent — protected from deletion"
                                                        >
                                                            <Shield size={18} />
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleOpenDeleteModal(aiAgent)}
                                                            className="w-10 h-10 flex items-center justify-center text-gray-400 bg-white border border-gray-100 hover:text-red-500 hover:bg-red-50 hover:border-red-100 rounded-xl transition-all shadow-sm"
                                                            title="Delete AI Agent"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            </div>

            {/* General Edit / Create Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-[200] p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-scaleIn border border-gray-100/50">
                        <div className="px-8 py-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/20">
                            <div>
                                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase">
                                    {editingAiAgent ? "Edit AI Agent" : "New AI Agent"}
                                </h3>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-0.5">AI Agent Details</p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-xl transition-all"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmitGeneral} className="p-8 space-y-5">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">AI Agent Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                                    placeholder="e.g. Support AI Agent"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Internal Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 text-xs transition-all resize-none font-medium"
                                    placeholder="Brief internal note regarding this AI agent..."
                                    rows={3}
                                />
                            </div>

                            <div className="flex items-center justify-center gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2.5 text-gray-500 hover:text-gray-900 font-bold text-[10px] uppercase tracking-widest transition-colors rounded-xl bg-gray-50 hover:bg-gray-100"
                                >
                                    Discard
                                </button>
                                <button
                                    type="submit"
                                    className="px-10 py-2.5 bg-gray-900 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95"
                                >
                                    {editingAiAgent ? "Update AI Agent" : "Create AI Agent"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Config JSON View Modal */}
            {isConfigModalOpen && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-[200] p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl animate-scaleIn border border-gray-100/50 flex flex-col h-[70vh]">
                        {/* Header */}
                        <div className="px-8 py-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/20">
                            <div>
                                <h3 className="text-lg font-black text-gray-900 tracking-tighter uppercase">
                                    Agent JSON Config: {configAgent?.name}
                                </h3>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-0.5">Raw Schema Representation</p>
                            </div>
                            <button
                                onClick={() => setIsConfigModalOpen(false)}
                                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-xl transition-all"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 p-8 overflow-hidden bg-[#fcfcfd]/20 relative flex flex-col">
                            {isConfigLoading ? (
                                <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                                    <div className="w-8 h-8 border-3 border-gray-100 border-t-gray-900 rounded-full animate-spin" />
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Compiling agent schema config...</p>
                                </div>
                            ) : agentJsonData ? (
                                <div className="flex-1 flex flex-col gap-4 min-h-0">
                                    <div className="flex justify-between items-center flex-shrink-0">
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Native Config JSON</p>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(JSON.stringify(agentJsonData, null, 2));
                                                showToast("JSON Config copied to clipboard");
                                            }}
                                            className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white hover:bg-black text-[9px] font-black uppercase tracking-widest rounded-xl transition-all shadow-sm active:scale-95"
                                        >
                                            <Copy size={12} />
                                            Copy JSON
                                        </button>
                                    </div>
                                    <div className="flex-1 bg-slate-950 rounded-2xl p-6 border border-slate-900 overflow-auto custom-scrollbar">
                                        <pre className="text-[11px] font-mono text-slate-300 leading-relaxed whitespace-pre-wrap select-all">
                                            {JSON.stringify(agentJsonData, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-10 text-xs font-bold text-gray-400 uppercase">
                                    Failed to retrieve config schema
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                isLoading={isLoading}
                title="Destroy AI Agent Hub?"
                message={`This action will permanently Delete '${aiAgentToDelete?.name}' and all its associated data. Are you prepared to proceed?`}
            />
        </>
    );
};

export default AiAgents;

